import { useAccount } from "#contexts/AccountContext"
import { useI18n } from "#hooks/use-i18n"
import { useIsDashboardEmpty, useOnboardingSetup } from "#hooks/use-onboarding-setup"
import { useTour } from "@reactour/tour"
import { RefreshCw } from "lucide-react"
import { useState } from "react"
import { Button } from "../ui/button"
import { Checkbox } from "../ui/checkbox"
import { Input } from "../ui/input"
import { Label } from "../ui/label"

interface OnboardingStepProps {
  onComplete: () => void
}

export function OnboardingStep({ onComplete }: OnboardingStepProps) {
  const { t } = useI18n()
  const { needsPartner, needsCategories, needsExpenses } = useIsDashboardEmpty()
  const { isReady } = useAccount()
  const [partnerName, setPartnerName] = useState("")
  const [partnerEmail, setPartnerEmail] = useState("")
  const [grossMonthlySalary, setGrossMonthlySalary] = useState("")
  const [netMonthlySalary, setNetMonthlySalary] = useState("")
  const [createCategories, setCreateCategories] = useState(needsCategories)
  const [createExpenses, setCreateExpenses] = useState(needsExpenses)
  const setup = useOnboardingSetup()
  const { setCurrentStep } = useTour()

  const handleSetup = async () => {
    try {
      await setup.mutateAsync({
        partnerName: needsPartner ? partnerName.trim() || undefined : undefined,
        partnerEmail: needsPartner && partnerName ? partnerEmail.trim() || undefined : undefined,
        grossMonthlySalary: needsPartner
          ? grossMonthlySalary.trim()
            ? parseFloat(grossMonthlySalary.trim()) || 0
            : 0
          : 0,
        netMonthlySalary: needsPartner
          ? netMonthlySalary.trim()
            ? parseFloat(netMonthlySalary.trim()) || 0
            : 0
          : 0,
        createDefaultCategories: needsCategories ? createCategories : false,
        createSampleExpenses: needsExpenses
          ? createExpenses && (needsCategories ? createCategories : true)
          : false,
      })

      setTimeout(() => {
        setCurrentStep(1)
        onComplete()
      }, 1000)
    } catch (error) {
      console.error("Failed to set up onboarding:", error)
    }
  }

  const isLoading = setup.isPending

  const hasAnyOption = needsPartner || needsCategories || needsExpenses

  if (!hasAnyOption) {
    return null
  }

  return (
    <div className="space-y-4">
      {needsPartner && (
        <>
          <div className="space-y-2">
            <Label htmlFor="partner-name">{t("components.onboarding.partnerName")}</Label>
            <Input
              id="partner-name"
              placeholder={t("components.onboarding.partnerNamePlaceholder")}
              value={partnerName}
              onChange={(e) => setPartnerName(e.target.value)}
              disabled={isLoading}
              required
            />
            <p className="text-xs text-muted-foreground">
              {t("components.onboarding.partnerNameDescription")}
            </p>
            {partnerName.trim() && partnerName.trim().split(" ").filter(Boolean).length < 2 && (
              <p className="text-xs text-destructive">
                {t("components.onboarding.partnerNameError")}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="partner-email">{t("components.onboarding.partnerEmail")}</Label>
            <Input
              id="partner-email"
              type="email"
              placeholder={t("components.onboarding.partnerEmailPlaceholder")}
              value={partnerEmail}
              onChange={(e) => setPartnerEmail(e.target.value)}
              disabled={isLoading}
              required
            />
          </div>
          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-2">
              <Label htmlFor="gross-salary">{t("components.onboarding.grossSalary")}</Label>
              <Input
                id="gross-salary"
                type="number"
                placeholder={t("components.onboarding.grossSalaryPlaceholder")}
                value={grossMonthlySalary}
                onChange={(e) => setGrossMonthlySalary(e.target.value)}
                disabled={isLoading}
                min="0"
                step="0.01"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="net-salary">{t("components.onboarding.netSalary")}</Label>
              <Input
                id="net-salary"
                type="number"
                placeholder={t("components.onboarding.netSalaryPlaceholder")}
                value={netMonthlySalary}
                onChange={(e) => setNetMonthlySalary(e.target.value)}
                disabled={isLoading}
                min="0"
                step="0.01"
                required
              />
            </div>
          </div>
        </>
      )}

      {needsCategories && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="create-categories"
              checked={createCategories}
              onCheckedChange={(checked) => setCreateCategories(checked === true)}
              disabled={isLoading}
            />
            <Label htmlFor="create-categories" className="text-sm font-normal cursor-pointer">
              {t("components.onboarding.createCategories")}
            </Label>
          </div>
          <p className="text-xs text-muted-foreground ml-6">
            {t("components.onboarding.categoriesDescription")}
          </p>
        </div>
      )}

      {needsExpenses && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="create-expenses"
              checked={createExpenses}
              onCheckedChange={(checked) => setCreateExpenses(checked === true)}
              disabled={isLoading || (needsCategories && !createCategories)}
            />
            <Label htmlFor="create-expenses" className="text-sm font-normal cursor-pointer">
              {t("components.onboarding.createExpenses")}
            </Label>
          </div>
          <p className="text-xs text-muted-foreground ml-6">
            {t("components.onboarding.expensesDescription")}
          </p>
        </div>
      )}

      {/* Button is enabled if account is ready and at least one visible option is selected */}
      <Button
        onClick={handleSetup}
        disabled={
          !isReady ||
          isLoading ||
          !(
            (needsPartner &&
              partnerName.trim() &&
              partnerName.trim().split(" ").filter(Boolean).length >= 2 &&
              partnerEmail.trim() &&
              grossMonthlySalary.trim() &&
              parseFloat(grossMonthlySalary.trim()) >= 0 &&
              netMonthlySalary.trim() &&
              parseFloat(netMonthlySalary.trim()) >= 0) ||
            (needsCategories && createCategories) ||
            (needsExpenses && createExpenses)
          )
        }
        className="w-full mt-4"
        size="lg"
      >
        {isLoading ? (
          <span className="flex items-center justify-center">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            {t("components.onboarding.settingUp")}
          </span>
        ) : (
          t("components.onboarding.getStarted")
        )}
      </Button>
      {setup.isError && (
        <div className="mt-2 text-sm text-destructive text-center">
          {setup.error instanceof Error
            ? setup.error.message
            : t("components.onboarding.setupError")}
        </div>
      )}
    </div>
  )
}
